import { format, parseISO } from 'date-fns';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowDown, ArrowUp, CaretDown, DownloadSimple, MagnifyingGlass, Plus, X as XIcon } from 'shared/icons';
import * as XLSX from 'xlsx';
import { PREDEFINED_COLUMNS } from '../../constants/presets';
import blingStockService from '../../services/BlingStockService';
import ColumnProcessor from '../../services/ColumnProcessor';
// import BlingStockColumn from './BlingStockColumn';
import mlStockService from '../../services/MlStockService';
// import CustomColumnBuilder from './CustomColumnBuilder';
import presetManager from '../../services/PresetManager';
import tagService from '../../services/TagService';
import { FilterMainDepositStockMap } from '../../utils/blingStockConfig';
import BlingStockModal from '../bling-stock/BlingStockModal';
// import { loadMainDepositIds } from '../../utils/blingStockConfig';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import { Skeleton } from '../ui/Skeleton';
import TableShell from '../ui/TableShell';
import PaginationControls from './PaginationControls';
import PresetModal from './PresetModal';
import PresetSelector from './PresetSelector';
import TagsColumn from './TagsColumn';

const SELLOUT_MAIN_BLING_DEPOSIT_IDS = [13772186008];

/**
 * ProductAnalysisTable
 *
 * Tabela de analise de produtos com vendas diarias, agregacoes temporais,
 * ordenacao por coluna e paginacao client-side.
 * Suporta presets customizaveis para selecao de colunas.
 */
const ProductAnalysisTable = ({ products, loading, onAddToCart }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [sortColumn, setSortColumn] = useState('last7d');
    const [sortDirection, setSortDirection] = useState('desc'); // 'asc' | 'desc' | null (sem ordenacao)
    const [itemsPerPage, setItemsPerPage] = useState(15);
    const [activePreset, setActivePreset] = useState(null);
    const [processedProducts, setProcessedProducts] = useState([]);
    const [isPresetModalOpen, setIsPresetModalOpen] = useState(false);
    const [editingPreset, setEditingPreset] = useState(null);

    // Estados para estoque Bling
    const [stockData, setStockData] = useState(new Map());
    const [stockLoading, setStockLoading] = useState(false);

    // Estados para estoque ML
    const [mlStockMap, setMlStockMap] = useState(new Map());
    const [mlStockLoading, setMlStockLoading] = useState(false);

    // Estados para pesquisa
    const [searchTerm, setSearchTerm] = useState('');
    const [searchFilter, setSearchFilter] = useState('all'); // 'all', 'tags', 'title', 'sku', 'inventoryId'
    const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
    const filterDropdownRef = useRef(null);

    // Estados para modal de estoque
    const [selectedProductForModal, setSelectedProductForModal] = useState(null);
    const [isStockModalOpen, setIsStockModalOpen] = useState(false);
    const [cartModal, setCartModal] = useState({
        isOpen: false,
        quantity: 1,
        product: null,
    });

    const columnProcessor = useMemo(() => new ColumnProcessor(), []);

    const calculateSalesForecast = useCallback((product) => {
        let bestDayQuantity = 0;
        let averageLast7d = 0;

        if (product?.dailySales instanceof Map && product.dailySales.size > 0) {
            const last7Entries = Array.from(product.dailySales.entries())
                .filter(([date, quantity]) => !Number.isNaN(new Date(date).getTime()) && typeof quantity === 'number')
                .sort((a, b) => new Date(b[0]).getTime() - new Date(a[0]).getTime())
                .slice(0, 7);

            if (last7Entries.length > 0) {
                const quantities = last7Entries.map(([, quantity]) => Number(quantity || 0));
                bestDayQuantity = Math.max(...quantities);
                averageLast7d = quantities.reduce((sum, quantity) => sum + quantity, 0) / 7;
            }
        }

        if (bestDayQuantity === 0) {
            bestDayQuantity = Number(product?.bestDay?.quantity || 0);
        }

        if (averageLast7d === 0) {
            averageLast7d =
                typeof product?.average === 'number' ? product.average : (Number(product?.last7d || 0) || 0) / 7;
        }

        return bestDayQuantity * 7 + averageLast7d * 15;
    }, []);

    const normalizeSkuKey = useCallback((value) => {
        const raw = String(value || '').trim();
        if (!raw) {
            return '';
        }

        try {
            return decodeURIComponent(raw).toUpperCase();
        } catch {
            const escaped = raw.replace(/%(?![0-9A-Fa-f]{2})/g, '%25');
            try {
                return decodeURIComponent(escaped).toUpperCase();
            } catch {
                return raw.toUpperCase();
            }
        }
    }, []);

    const getMapEntryBySku = useCallback(
        (map, sellerSku) => {
            if (!(map instanceof Map)) {
                return null;
            }

            const raw = String(sellerSku || '').trim();
            if (!raw) {
                return null;
            }

            if (map.has(raw)) {
                return map.get(raw);
            }

            const normalized = normalizeSkuKey(raw);
            if (normalized && map.has(normalized)) {
                return map.get(normalized);
            }

            const target = normalized || raw.toUpperCase();
            for (const [key, value] of map.entries()) {
                const candidate = normalizeSkuKey(key);
                if (candidate && candidate === target) {
                    return value;
                }
            }

            return null;
        },
        [normalizeSkuKey],
    );

    // Inicializar preset manager
    useEffect(() => {
        const initPresets = async () => {
            try {
                await presetManager.initialize();
                setActivePreset(presetManager.getActivePreset());
            } catch (error) {
                console.error('Erro ao inicializar presets:', error);
            }
        };

        initPresets();

        // Listener para mudancas de preset
        const handlePresetChange = () => {
            setActivePreset(presetManager.getActivePreset());
        };

        presetManager.addListener(handlePresetChange);

        return () => {
            presetManager.removeListener(handlePresetChange);
        };
    }, []);

    // Processar produtos com colunas do preset ativo
    useEffect(() => {
        if (!products || products.length === 0 || !activePreset) {
            setProcessedProducts(products || []);
            return;
        }

        try {
            const processed = columnProcessor.processColumns(products, activePreset.columns);
            setProcessedProducts(
                processed.map((product) => ({
                    ...product,
                    salesForecast: calculateSalesForecast(product),
                })),
            );
        } catch (error) {
            console.error('Erro ao processar colunas:', error);
            setProcessedProducts(
                (products || []).map((product) => ({
                    ...product,
                    salesForecast: calculateSalesForecast(product),
                })),
            );
        }
    }, [products, activePreset, columnProcessor, calculateSalesForecast]);

    // Funcao helper para extrair seller_sku de um produto
    const extractSellerSku = useCallback((product) => {
        if (!product || typeof product !== 'object') {
            return null;
        }

        return (
            product.seller_sku ||
            product.sellerSku ||
            product.item_seller_sku ||
            product.sku ||
            product.item?.seller_sku ||
            product.item?.item_seller_sku ||
            product.listing?.seller_sku ||
            product.variation?.seller_sku ||
            product.attributes?.seller_sku
        );
    }, []);

    // Funcao para agrupar produtos por SKU
    const groupProductsBySku = useCallback(
        (products) => {
            const skuMap = new Map();

            products.forEach((product) => {
                const sku = extractSellerSku(product);

                if (!sku) {
                    // Produtos sem SKU mantem como estao
                    const uniqueKey = `no-sku-${product.itemId}`;
                    skuMap.set(uniqueKey, product);
                    return;
                }

                if (!skuMap.has(sku)) {
                    // Primeiro produto com este SKU
                    skuMap.set(sku, {
                        ...product,
                        _groupedBy: 'sku',
                        _originalCount: 1,
                        _itemIds: [product.itemId],
                    });
                } else {
                    // Agregar vendas com produto existente
                    const existing = skuMap.get(sku);

                    // Somar todas as metricas numericas
                    const numericFields = [
                        'today',
                        'yesterday',
                        'day2',
                        'day3',
                        'last3d',
                        'last5d',
                        'last7d',
                        'last15d',
                        'last30d',
                        'last60d',
                        'last90d',
                        'last180d',
                        'last365d',
                        'average',
                        'currentPeriod',
                        'previousPeriod',
                        'twoPeriodsPast',
                    ];

                    numericFields.forEach((field) => {
                        if (typeof existing[field] === 'number' && typeof product[field] === 'number') {
                            existing[field] += product[field];
                        }
                    });

                    // Atualizar bestDay se necessario
                    if (product.bestDay && existing.bestDay) {
                        if (product.bestDay.quantity > existing.bestDay.quantity) {
                            existing.bestDay = product.bestDay;
                        }
                    }

                    // Recalcular media
                    existing.average = existing.last7d / 7;
                    existing.salesForecast = calculateSalesForecast(existing);

                    // Manter lista de itemIds agrupados
                    existing._originalCount++;
                    existing._itemIds.push(product.itemId);

                    // Atualizar titulo para mostrar que e agrupado
                    if (existing._originalCount === 2) {
                        existing.title = `${existing.title} (+1 anuncio)`;
                    } else if (existing._originalCount > 2) {
                        existing.title = existing.title.replace(
                            /\(\+\d+ anuncios?\)/,
                            `(+${existing._originalCount - 1} anuncios)`,
                        );
                    }
                }
            });

            return Array.from(skuMap.values());
        },
        [extractSellerSku, calculateSalesForecast],
    );

    // Funcao para obter estoque de um SKU
    const getStockForSku = useCallback(
        (sellerSku) => {
            if (!sellerSku || typeof sellerSku !== 'string' || !sellerSku.trim()) {
                return {
                    quantity: 'N/A',
                    status: 'not-configured',
                    brand: null,
                    error: null,
                };
            }

            const trimmedSku = sellerSku.trim();
            const stockInfo = getMapEntryBySku(stockData, trimmedSku);

            if (!stockInfo) {
                // Se esta carregando E ainda nao temos dados, mostrar loading
                // Se nao esta carregando OU ja temos alguns dados, mostrar N/A
                const shouldShowLoading = stockLoading && stockData.size === 0;
                return {
                    quantity: shouldShowLoading ? '...' : 'N/A',
                    status: shouldShowLoading ? 'loading' : 'not-configured',
                    brand: null,
                    error: null,
                };
            }

            // Garantir que quantity seja um numero
            const quantity =
                typeof stockInfo.quantity === 'number'
                    ? stockInfo.quantity
                    : typeof stockInfo.quantity === 'string' && stockInfo.quantity !== 'N/A'
                      ? Number.parseInt(stockInfo.quantity, 10) || 0
                      : 0;

            return {
                quantity: quantity,
                price: stockInfo.price || 0,
                costPrice: stockInfo.costPrice || 0,
                brand: stockInfo.brand || null,
                status: stockInfo.status || 'available',
                error: stockInfo.error,
            };
        },
        [stockData, stockLoading, getMapEntryBySku],
    );

    const mlItemIdsBySku = useMemo(() => {
        const grouped = new Map();
        for (const product of processedProducts || []) {
            const sku = normalizeSkuKey(extractSellerSku(product));
            if (!sku) {
                continue;
            }
            const itemId = String(product?.itemId || product?.item_id || '').trim().toUpperCase();
            if (!itemId) {
                continue;
            }
            if (!grouped.has(sku)) {
                grouped.set(sku, new Set());
            }
            grouped.get(sku).add(itemId);
        }
        return grouped;
    }, [processedProducts, extractSellerSku, normalizeSkuKey]);

    // Obter colunas do preset ativo
    const visibleColumns = useMemo(() => {
        if (!activePreset || !activePreset.columns) {
            return [];
        }
        return activePreset.columns;
    }, [activePreset]);

    // Funcao para atualizar estoque manualmente
    // const handleRefreshStock = useCallback(
    //   async (specificSku = null) => {
    //     try {
    //       setStockLoading(true);
    //       setMlStockLoading(true);

    //       const firstColumn = visibleColumns[0];
    //       const isSkuFirstColumn = firstColumn && firstColumn.key === 'sku';

    //       if (specificSku) {
    //         console.log('Atualizando estoque para SKU:', specificSku);

    //         const product = processedProducts.find(
    //           p => extractSellerSku(p) === specificSku
    //         );

    //         if (product && isMlProduct(product)) {
    //           const sku = extractSellerSku(product);
    //           if (sku) {
    //             const mlStockResult = await mlStockService.getBatchStockByIdentifiers([sku]);
    //             console.log('RODOU AQUI 1') // NAO AOCNTECE
    //             setMlStockMap(prevMap => new Map([...prevMap, ...mlStockResult]));
    //             setStockData(prevData => new Map([...prevData, ...mlStockResult]));
    //           }
    //         } else {
    //           const stockMap = await blingStockService.refreshStock([specificSku]);
    //           setStockData(prevData => {
    //             const newData = new Map(prevData);
    //             stockMap.forEach((value, key) => {
    //               newData.set(key, value);
    //             });
    //             return newData;
    //           });
    //         }
    //       } else {
    //         console.log('Atualizando todos os estoques');
    //         const blingProducts = processedProducts.filter(p =>
    //           isBlingProduct(p)
    //         );
    //         const mlProducts = processedProducts.filter(p => isMlProduct(p));

    //         // Garantir que produtos Bling tenham seller_sku formatado corretamente
    //         const blingProductsWithSku = blingProducts.map(p => {
    //           const sku = extractSellerSku(p);
    //           return {
    //             ...p,
    //             seller_sku: sku || p.seller_sku || p.sellerSku || p.sku || p.item_seller_sku
    //           };
    //         }).filter(p => p.seller_sku); // Remover produtos sem SKU

    //         const mlIdentifiers = mlProducts
    //           .map(p => extractSellerSku(p))
    //           .filter(Boolean);

    //         const [blingStockResult, mlStockResult] = await Promise.all([
    //           blingProductsWithSku.length > 0
    //             ? blingStockService.getStockForProducts(blingProductsWithSku)
    //             : Promise.resolve(new Map()),
    //           mlIdentifiers.length > 0
    //             ? mlStockService.getBatchStockByIdentifiers(mlIdentifiers)
    //             : Promise.resolve(new Map()),
    //         ]);

    //         // Filtrar estoque Bling para usar apenas depositos principais (mesma logica do Stock)
    //         const mainDepositIds = loadMainDepositIds();
    //         const filteredBlingStock = filterMainDepositStockMap(blingStockResult, mainDepositIds);

    //         const combinedStockMap = new Map([
    //           ...filteredBlingStock,
    //           ...mlStockResult,
    //         ]);
    //         setStockData(combinedStockMap);
    //         setMlStockMap(mlStockResult);
    //         console.log('RODOU AQUI 2') // NAO ACONTECE
    //       }
    //     } catch (error) {
    //       console.error(' Erro ao atualizar estoque:', error);
    //     } finally {
    //       setStockLoading(false);
    //       setMlStockLoading(false);
    //     }
    //   },
    //   [
    //     processedProducts,
    //     isBlingProduct,
    //     isMlProduct,
    //     extractSellerSku,
    //     visibleColumns,
    //   ]
    // );

    // Sistema de atualizacao removido conforme solicitado

    // Obter colunas de identificacao para o dropdown
    const identificationColumns = useMemo(() => {
        return Object.entries(PREDEFINED_COLUMNS)
            .filter(([_, col]) => col.category === 'identification')
            .map(([key, col]) => ({ key, label: col.label }));
    }, []);

    // Fechar dropdown ao clicar fora
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target)) {
                setIsFilterDropdownOpen(false);
            }
        };

        if (isFilterDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isFilterDropdownOpen]);

    // Obter label do filtro selecionado
    const getFilterLabel = (filterValue) => {
        if (filterValue === 'all') {
            return 'Todas';
        }
        if (filterValue === 'tags') {
            return 'Tags';
        }
        const column = identificationColumns.find((col) => col.key === filterValue);
        return column ? column.label : 'Todas';
    };

    // Obter placeholder baseado no filtro
    const getSearchPlaceholder = () => {
        if (searchFilter === 'all') {
            return 'Pesquisar por MLB, SKU, ID do Inventario ou Tags...';
        }
        if (searchFilter === 'tags') {
            return 'Pesquisar por Tags...';
        }
        const column = identificationColumns.find((col) => col.key === searchFilter);
        return column ? `Pesquisar por ${column.label}...` : 'Pesquisar...';
    };

    const openCartModal = (product) => {
        setCartModal({
            isOpen: true,
            quantity: 1,
            product,
        });
    };

    const closeCartModal = () => {
        setCartModal({
            isOpen: false,
            quantity: 1,
            product: null,
        });
    };

    const confirmAddToCart = () => {
        if (typeof onAddToCart !== 'function' || !cartModal.product) {
            closeCartModal();
            return;
        }

        const quantity = Math.floor(Number(cartModal.quantity || 0));
        if (!Number.isFinite(quantity) || quantity <= 0) {
            return;
        }

        const resolvedSku = extractSellerSku(cartModal.product) || null;
        const stockSnapshot = getStockForSku(resolvedSku);
        const resolvedTitle =
            String(cartModal.product.title || '').trim() ||
            String(cartModal.product.name || '').trim() ||
            resolvedSku ||
            'Produto';

        onAddToCart({
            sku: resolvedSku,
            item_id: cartModal.product.itemId || cartModal.product.item_id || null,
            item_ids: Array.from(
                mlItemIdsBySku.get(normalizeSkuKey(resolvedSku)) || new Set(),
            ),
            inventory_id: cartModal.product.inventoryId || null,
            title: resolvedTitle,
            product_name: resolvedTitle,
            bling_stock: Number(stockSnapshot.quantity || 0),
            cost_price: Number(stockSnapshot.costPrice || cartModal.product?.cost_price || 0),
            quantity,
        });

        closeCartModal();
    };

    // Filtrar produtos baseado na pesquisa
    const filteredProducts = useMemo(() => {
        if (!processedProducts || processedProducts.length === 0) {
            return [];
        }
        if (!searchTerm.trim()) {
            return processedProducts;
        }

        const term = searchTerm.trim().toLowerCase();

        return processedProducts.filter((product) => {
            // Se esta pesquisando por tag
            if (searchFilter === 'tags') {
                // Obter identificadores visiveis
                const identifiers = [];
                visibleColumns.forEach((column) => {
                    if (column.type === 'predefined') {
                        if (column.key === 'title' || column.key === 'itemId') {
                            const itemId = product.itemId || product.item_id;
                            if (itemId) {
                                identifiers.push({ type: 'itemId', value: itemId });
                            }
                        } else if (column.key === 'sku') {
                            const sku = extractSellerSku(product);
                            if (sku) {
                                identifiers.push({ type: 'sku', value: sku });
                            }
                        } else if (column.key === 'inventoryId') {
                            const inventoryId = product.inventoryId;
                            if (inventoryId) {
                                identifiers.push({ type: 'inventoryId', value: inventoryId });
                            }
                        }
                    }
                });

                if (identifiers.length === 0) {
                    return false;
                }

                // Verificar se alguma tag corresponde
                const allTags = tagService.getTagsForMultiple(identifiers);
                return allTags.some((tag) => tag.toLowerCase().includes(term));
            }
            // Se esta pesquisando por uma coluna especifica
            if (searchFilter !== 'all') {
                if (searchFilter === 'title' || searchFilter === 'itemId') {
                    // Pesquisar pela descricao (title) e nao pelo codigo MLB
                    const title = (product.title || '').toLowerCase();
                    return title.includes(term);
                }
                if (searchFilter === 'sku') {
                    const sku = (extractSellerSku(product) || '').toLowerCase();
                    return sku.includes(term);
                }
                if (searchFilter === 'inventoryId') {
                    const inventoryId = (product.inventoryId || '').toLowerCase();
                    return inventoryId.includes(term);
                }
                if (searchFilter === 'brand') {
                    const sku = extractSellerSku(product);
                    const brand = (getStockForSku(sku).brand || '').toLowerCase();
                    return brand.includes(term);
                }
            }
            // Pesquisa em todas as colunas
            else {
                const itemId = (product.itemId || product.item_id || '').toLowerCase();
                const title = (product.title || '').toLowerCase();
                const sku = (extractSellerSku(product) || '').toLowerCase();
                const inventoryId = (product.inventoryId || '').toLowerCase();
                const brand = (getStockForSku(extractSellerSku(product)).brand || '').toLowerCase();

                return (
                    itemId.includes(term) ||
                    title.includes(term) ||
                    sku.includes(term) ||
                    inventoryId.includes(term) ||
                    brand.includes(term)
                );
            }

            return false;
        });
    }, [processedProducts, searchTerm, searchFilter, visibleColumns, extractSellerSku, getStockForSku]);

    // Ordenar produtos
    const sortedProducts = useMemo(() => {
        if (!filteredProducts || filteredProducts.length === 0) {
            return [];
        }

        // Verificar se a PRIMEIRA coluna e SKU (coluna de identificacao)
        const firstColumn = visibleColumns[0];
        const isSkuFirstColumn = firstColumn && firstColumn.key === 'sku';

        let productsToSort = [...filteredProducts];

        // Agrupar por SKU APENAS se SKU for a primeira coluna (identificacao)
        if (isSkuFirstColumn) {
            // console.log(' [Agrupamento] SKU e a primeira coluna - agrupando por SKU...');
            // console.log(' [Agrupameanto] Produtos antes:', productsToSort.length);
            productsToSort = groupProductsBySku(productsToSort);
            // console.log(' [Agrupaumento] Produtos depois:', productsToSort.length);
            // } else {
            //   console.log(' [Agrupamento] Primeira coluna nao e SKU - mostrando todos os anuncios separadamente');
            //   console.log(' [Agrupamento] Primeira coluna:', firstColumn?.key);
        }

        const sorted = [...productsToSort].sort((a, b) => {
            let aValue;
            let bValue;

            // Verificar se e coluna customizada
            if (sortColumn.startsWith('custom_')) {
                aValue = a[sortColumn];
                bValue = b[sortColumn];
            } else {
                aValue = a[sortColumn];
                bValue = b[sortColumn];
            }

            // Tratamento especial para bestDay (objeto)
            if (sortColumn === 'bestDay') {
                aValue = a.bestDay?.quantity || 0;
                bValue = b.bestDay?.quantity || 0;

                if (!sortDirection) {
                    return 0; // Sem ordenacao
                }
            }

            // Tratamento especial para title (string)
            if (sortColumn === 'title') {
                if (!sortDirection) {
                    return 0; // Sem ordenacao
                }

                return sortDirection === 'asc'
                    ? (aValue || '').localeCompare(bValue || '')
                    : (bValue || '').localeCompare(aValue || '');
            }

            // Tratamento especial para sku (string)
            if (sortColumn === 'sku') {
                if (!sortDirection) {
                    return 0; // Sem ordenacao
                }

                const skuA = extractSellerSku(a) || '';
                const skuB = extractSellerSku(b) || '';
                return sortDirection === 'asc' ? skuA.localeCompare(skuB) : skuB.localeCompare(skuA);
            }

            if (sortColumn === 'brand') {
                if (!sortDirection) {
                    return 0;
                }

                const brandA = (getStockForSku(extractSellerSku(a)).brand || a.brand || '').toLowerCase();
                const brandB = (getStockForSku(extractSellerSku(b)).brand || b.brand || '').toLowerCase();
                return sortDirection === 'asc' ? brandA.localeCompare(brandB) : brandB.localeCompare(brandA);
            }

            // Tratamento especial para mlStock
            if (sortColumn === 'mlStock' || sortColumn === 'mlStockAvailable' || sortColumn === 'mlStockNotAvailable') {
                const getStockValue = (product) => {
                    // Sempre usar SKU para buscar no mlStockMap
                    const sku = extractSellerSku(product);
                    if (!sku) {
                        return -1; // Sem SKU vai para o final
                    }

                    const stockInfo = getMapEntryBySku(mlStockMap, sku);
                    if (!stockInfo) {
                        return -1; // Nao encontrado vai para o final
                    }

                    // Retornar valor baseado na coluna
                    if (sortColumn === 'mlStock') {
                        return typeof stockInfo.total === 'number'
                            ? stockInfo.total
                            : typeof stockInfo.quantity === 'number'
                              ? stockInfo.quantity
                              : 0;
                    }
                    if (sortColumn === 'mlStockAvailable') {
                        return typeof stockInfo.available_quantity === 'number'
                            ? stockInfo.available_quantity
                            : typeof stockInfo.quantity === 'number'
                              ? stockInfo.quantity
                              : 0;
                    }
                    if (sortColumn === 'mlStockNotAvailable') {
                        return typeof stockInfo.not_available_quantity === 'number'
                            ? stockInfo.not_available_quantity
                            : 0;
                    }
                    return 0;
                };

                const stockA = getStockValue(a);
                const stockB = getStockValue(b);

                if (sortDirection === 'asc') {
                    return stockA - stockB;
                }
                if (sortDirection === 'desc') {
                    return stockB - stockA;
                }
                // Se sortDirection for null, nao ordenar (manter ordem original)
                return 0;
            }

            // Tratamento especial para blingStock
            if (sortColumn === 'blingStock') {
                const getStockValue = (product) => {
                    const sellerSku = extractSellerSku(product);
                    if (!sellerSku) {
                        return -1; // N/A vai para o final
                    }

                    const stockInfo = getMapEntryBySku(stockData, sellerSku);
                    if (!stockInfo) {
                        return -1;
                    }
                    if (stockInfo.status === 'error') {
                        return -1;
                    }
                    if (stockInfo.status === 'not-found') {
                        return 0;
                    }

                    return stockInfo.quantity || 0;
                };

                const stockA = getStockValue(a);
                const stockB = getStockValue(b);

                if (sortDirection === 'asc') {
                    return stockA - stockB;
                }
                if (sortDirection === 'desc') {
                    return stockB - stockA;
                }
                // Se sortDirection for null, nao ordenar (manter ordem original)
                return 0;
            }

            // Se sortDirection for null, nao ordenar (manter ordem original)
            if (!sortDirection) {
                return 0;
            }

            // Ordenacao numerica
            const numA = Number(aValue) || 0;
            const numB = Number(bValue) || 0;

            if (sortDirection === 'asc') {
                return numA - numB;
            }
            return numB - numA;
        });

        return sorted;
    }, [
        filteredProducts,
        sortColumn,
        sortDirection,
        stockData,
        mlStockMap,
        visibleColumns,
        groupProductsBySku,
        extractSellerSku,
        getMapEntryBySku,
        getStockForSku,
    ]);

    // Produtos da pagina atual
    const currentPageProducts = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        const end = start + itemsPerPage;
        return sortedProducts.slice(start, end);
    }, [sortedProducts, currentPage, itemsPerPage]);

    // Total de paginas
    const totalPages = Math.ceil(sortedProducts.length / itemsPerPage);

    // Controle para evitar loops: track SKUs ja carregados
    const fetchedBlingSkusRef = useRef(new Set());
    const fetchedMlIdsRef = useRef(new Set());

    // Reset controles quando o conjunto de produtos muda (novo periodo/filtro)
    useEffect(() => {
        fetchedBlingSkusRef.current = new Set();
        fetchedMlIdsRef.current = new Set();
    }, [processedProducts]);

    useEffect(() => {
        if (!processedProducts || processedProducts.length === 0) {
            setStockData(new Map());
            setMlStockMap(new Map());
            return;
        }

        async function fetchStock() {
            setStockLoading(true);
            setMlStockLoading(true);

            const skus = [
                ...new Set(
                    processedProducts
                        .map(extractSellerSku)
                        .map((sku) => String(sku || '').trim())
                        .filter(Boolean),
                ),
            ];

            try {
                const [blingResult, mlResult] = await Promise.allSettled([
                    blingStockService.refreshStock(skus) ?? Promise.resolve(new Map()),
                    mlStockService.getBatchStockByIdentifiers(skus) ?? Promise.resolve(new Map()),
                ]);

                if (blingResult.status === 'fulfilled') {
                    const normalizedBlingStock =
                        blingResult.value instanceof Map
                            ? FilterMainDepositStockMap(blingResult.value, SELLOUT_MAIN_BLING_DEPOSIT_IDS)
                            : new Map();
                    setStockData(normalizedBlingStock instanceof Map ? normalizedBlingStock : new Map());
                } else {
                    console.error('Erro ao carregar estoque Bling:', blingResult.reason);
                }

                if (mlResult.status === 'fulfilled') {
                    setMlStockMap(mlResult.value instanceof Map ? mlResult.value : new Map());
                } else {
                    console.error('Erro ao carregar estoque ML:', mlResult.reason);
                }
            } catch (error) {
                console.error('Erro ao carregar estoque:', error);
            } finally {
                setStockLoading(false);
                setMlStockLoading(false);
            }
        }

        fetchStock();
    }, [processedProducts, extractSellerSku]);

    // Handler de ordenacao com ciclo de 3 estados: asc -> desc -> null (sem ordenacao)
    const handleSort = (column) => {
        if (sortColumn === column) {
            // Ciclar entre os 3 estados: asc -> desc -> null -> asc...
            if (sortDirection === 'asc') {
                setSortDirection('desc');
            } else if (sortDirection === 'desc') {
                setSortDirection(null); // Desativar ordenacao
            } else {
                // null -> asc
                setSortDirection('asc');
            }
        } else {
            // Nova coluna, comecar com desc
            setSortColumn(column);
            setSortDirection('desc');
        }
        // Resetar para primeira pagina
        setCurrentPage(1);
    };

    // Handlers de preset
    const handleCreatePreset = () => {
        setEditingPreset(null);
        setIsPresetModalOpen(true);
    };

    const handleEditPreset = (preset) => {
        setEditingPreset(preset);
        setIsPresetModalOpen(true);
    };

    // Handler de mudanca de pagina
    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);

        // Scroll para o inicio da tabela em vez do topo da pagina
        setTimeout(() => {
            const tableElement = document.querySelector('table');
            if (tableElement) {
                const tableTop = tableElement.getBoundingClientRect().top + window.pageYOffset;
                const offset = 100; // Offset para nao ficar colado no topo
                window.scrollTo({
                    top: Math.max(0, tableTop - offset),
                    behavior: 'smooth',
                });
            }
        }, 100); // Pequeno delay para garantir que a tabela foi renderizada
    };

    // Funcao de exportar para Excel
    const handleExport = () => {
        if (!activePreset || !visibleColumns.length) {
            alert('Nenhum preset ativo');
            return;
        }

        // Verificar se ha colunas de estoque e se ainda estao carregando
        const hasBlingStockColumn = visibleColumns.some((col) => col.key === 'blingStock');
        const hasMlStockColumn = visibleColumns.some(
            (col) => col.key === 'mlStock' || col.key === 'mlStockAvailable' || col.key === 'mlStockNotAvailable',
        );
        if ((hasBlingStockColumn || hasMlStockColumn) && (stockLoading || mlStockLoading)) {
            alert('Aguarde o carregamento do estoque antes de exportar');
            return;
        }

        // Funcoes helper locais para export
        const extractSellerSkuLocal = (product) => extractSellerSku(product);

        const getStockForSkuLocal = (sellerSku) => {
            if (!sellerSku || typeof sellerSku !== 'string' || !sellerSku.trim()) {
                return { quantity: 'N/A', price: 0, costPrice: 0, brand: null };
            }
            const trimmedSku = sellerSku.trim();
            const stockInfo = getMapEntryBySku(stockData, trimmedSku);

            if (!stockInfo) {
                return { quantity: 'N/A', price: 0, costPrice: 0, brand: null };
            }

            const quantity =
                typeof stockInfo.quantity === 'number'
                    ? stockInfo.quantity
                    : typeof stockInfo.quantity === 'string' && stockInfo.quantity !== 'N/A'
                      ? Number.parseInt(stockInfo.quantity, 10) || 0
                      : 0;

            return {
                quantity: quantity,
                price: stockInfo.price || 0,
                costPrice: stockInfo.costPrice || 0,
                brand: stockInfo.brand || null,
            };
        };

        const getMlStockLocal = (product) => {
            // Sempre usar SKU para buscar no mlStockMap
            const sellerSku = extractSellerSku(product);
            if (!sellerSku) {
                return 'N/A';
            }
            const stockInfo = getMapEntryBySku(mlStockMap, sellerSku);
            return stockInfo ? (stockInfo.total ?? stockInfo.quantity ?? 0) : 0;
        };

        const getMlStockAvailable = (product) => {
            // Sempre usar SKU para buscar no mlStockMap
            const sellerSku = extractSellerSku(product);
            if (!sellerSku) {
                return 'N/A';
            }
            const stockInfo = getMapEntryBySku(mlStockMap, sellerSku);
            return stockInfo ? (stockInfo.available_quantity ?? stockInfo.quantity ?? 0) : 0;
        };

        const getMlStockNotAvailable = (product) => {
            // Sempre usar SKU para buscar no mlStockMap
            const sellerSku = extractSellerSku(product);
            if (!sellerSku) {
                return 'N/A';
            }
            const stockInfo = getMapEntryBySku(mlStockMap, sellerSku);
            return stockInfo ? (stockInfo.not_available_quantity ?? 0) : 0;
        };

        // const getMlStockInfo = (product) => {
        //   // Sempre usar SKU para buscar no mlStockMap
        //   const sellerSku = extractSellerSku(product);
        //   if (!sellerSku) return null;
        //   return mlStockMap.get(sellerSku) || null;
        // };

        // Funcao helper para obter tags do produto
        const getProductTags = (product) => {
            const identifiers = [];

            visibleColumns.forEach((column) => {
                if (column.type === 'predefined') {
                    if (column.key === 'title' || column.key === 'itemId') {
                        const itemId = product.itemId || product.item_id;
                        if (itemId) {
                            identifiers.push({ type: 'itemId', value: itemId });
                        }
                    } else if (column.key === 'sku') {
                        const sku = extractSellerSkuLocal(product);
                        if (sku) {
                            identifiers.push({ type: 'sku', value: sku });
                        }
                    } else if (column.key === 'inventoryId') {
                        const inventoryId = product.inventoryId;
                        if (inventoryId) {
                            identifiers.push({ type: 'inventoryId', value: inventoryId });
                        }
                    }
                }
            });

            if (identifiers.length === 0) {
                return '';
            }

            const tags = tagService.getTagsForMultiple(identifiers);
            return tags.join(', ');
        };

        // Preparar dados para exportacao (TODOS os sortedProducts, sem paginacao)
        // Usar sortedProducts ao inves de currentPageProducts para exportar tudo
        const exportData = sortedProducts.map((product) => {
            const row = {};
            let isFirstColumn = true;

            visibleColumns.forEach((column) => {
                const label = column.type === 'predefined' ? column.label : column.name;

                if (column.type === 'predefined') {
                    if (column.key === 'itemId') {
                        row[label] = product.itemId || product.item_id || '-';
                    } else if (column.key === 'sku') {
                        row[label] = extractSellerSkuLocal(product) || '-';
                    } else if (column.key === 'bestDay') {
                        // DIVIDIR em duas colunas
                        if (product.bestDay?.date) {
                            row[`${label} - Data`] = format(parseISO(product.bestDay.date), 'dd/MM/yyyy');
                            row[`${label} - Qtd`] = product.bestDay.quantity;
                        } else {
                            row[`${label} - Data`] = '-';
                            row[`${label} - Qtd`] = '-';
                        }
                    } else if (column.key === 'blingStock') {
                        const sellerSku = extractSellerSkuLocal(product);
                        const stockInfo = getStockForSkuLocal(sellerSku);
                        row[label] = stockInfo.quantity;
                    } else if (column.key === 'brand') {
                        const sellerSku = extractSellerSkuLocal(product);
                        const stockInfo = getStockForSkuLocal(sellerSku);
                        row[label] = stockInfo.brand || '-';
                    } else if (column.key === 'mlStock') {
                        row[label] = getMlStockLocal(product);
                    } else if (column.key === 'mlStockAvailable') {
                        row[label] = getMlStockAvailable(product);
                    } else if (column.key === 'mlStockNotAvailable') {
                        row[label] = getMlStockNotAvailable(product);
                    } else if (column.key === 'blingPrice') {
                        const sellerSku = extractSellerSkuLocal(product);
                        const stockInfo = getStockForSkuLocal(sellerSku);
                        row[label] = stockInfo.price > 0 ? `R$ ${stockInfo.price.toFixed(2)}` : '-';
                    } else if (column.key === 'blingCostPrice') {
                        const sellerSku = extractSellerSkuLocal(product);
                        const stockInfo = getStockForSkuLocal(sellerSku);
                        row[label] = stockInfo.costPrice > 0 ? `R$ ${stockInfo.costPrice.toFixed(2)}` : '-';
                    } else if (column.key === 'blingMargin') {
                        const sellerSku = extractSellerSkuLocal(product);
                        const stockInfo = getStockForSkuLocal(sellerSku);
                        const costPrice = stockInfo.costPrice || 0;
                        const mlPrice = product.unit_price || product.price || 0;
                        let margin = null;
                        if (mlPrice > 0 && costPrice > 0) {
                            margin = ((mlPrice - costPrice) / mlPrice) * 100;
                        }
                        row[label] = margin !== null ? `${margin.toFixed(1)}%` : '-';
                    } else if (column.key === 'salesForecast') {
                        const forecast = Number(product.salesForecast || 0);
                        row[label] = forecast.toFixed(2);
                    } else {
                        row[label] =
                            product[column.key] !== undefined && product[column.key] !== null
                                ? product[column.key]
                                : '-';
                    }
                } else if (column.type === 'custom') {
                    const value = product[`custom_${column.id}`];
                    row[label] = columnProcessor.getDisplayValue(value, column.format);
                }

                // Adicionar coluna Tags apos a primeira coluna
                if (isFirstColumn) {
                    row.Tags = getProductTags(product);
                    isFirstColumn = false;
                }
            });

            return row;
        });

        // Criar workbook e worksheet
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(exportData);

        // Ajustar largura das colunas dinamicamente
        const headers = exportData.length > 0 ? Object.keys(exportData[0]) : [];
        const colWidths = headers.map((_, index) => {
            return index === 0 ? { wch: 50 } : { wch: 15 }; // Primeira coluna mais larga
        });
        ws['!cols'] = colWidths;

        // Adicionar worksheet ao workbook
        XLSX.utils.book_append_sheet(wb, ws, 'Analise de Produtos');

        // Gerar nome do arquivo com data
        const fileName = `analise-produtos-${format(new Date(), 'yyyy-MM-dd-HHmm')}.xlsx`;

        // Fazer download
        XLSX.writeFile(wb, fileName);
    };

    // Componente de cabecalho ordenavel com tooltip
    const SortableHeader = ({ column, children, tooltip, className = '' }) => {
        const isActive = sortColumn === column && sortDirection !== null;

        return (
            <th
                onClick={() => handleSort(column)}
                className={`px-3 py-3 text-center text-xs font-medium text-mono-600 dark:text-mono-400 uppercase tracking-wider cursor-pointer hover:bg-mono-100 dark:hover:bg-mono-900 transition-colors select-none ${className}`}
                title={tooltip}
            >
                <div className="flex items-center justify-center gap-1">
                    {children}
                    {isActive &&
                        (sortDirection === 'asc' ? (
                            <ArrowUp size={14} weight="bold" className="text-blue-600 dark:text-blue-400" />
                        ) : (
                            <ArrowDown size={14} weight="bold" className="text-blue-600 dark:text-blue-400" />
                        ))}
                </div>
            </th>
        );
    };

    // Loading state
    if (loading) {
        return (
            <div className="bg-white dark:bg-mono-950 border border-mono-200 dark:border-mono-800 rounded-xl p-12 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black dark:border-white mx-auto mb-4" />
                <p className="text-mono-600 dark:text-mono-400">Processando dados dos produtos...</p>
            </div>
        );
    }

    // Empty state
    if (!products || products.length === 0) {
        return (
            <div className="bg-white dark:bg-mono-950 border border-mono-200 dark:border-mono-800 rounded-xl p-12 text-center">
                <p className="text-mono-600 dark:text-mono-400">Nenhum produto encontrado nos pedidos</p>
            </div>
        );
    }

    const hasCartAction = typeof onAddToCart === 'function';

    return (
        <>
            <TableShell tableWrapClassName="">
                {/* Info header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-mono-200 dark:border-mono-800 gap-4">
                    <div className="flex items-center gap-4 flex-shrink-0">
                        <p className="text-sm text-mono-700 dark:text-mono-300 whitespace-nowrap">
                            <span className="font-semibold text-black dark:text-white">{sortedProducts.length}</span>{' '}
                            produtos encontrados
                            {totalPages > 1 && (
                                <span className="text-mono-500 dark:text-mono-500 ml-2">
                                    (exibindo {currentPageProducts.length} de {sortedProducts.length})
                                </span>
                            )}
                        </p>

                        {/* Preset Selector */}
                        <PresetSelector onCreatePreset={handleCreatePreset} onEditPreset={handleEditPreset} />
                    </div>

                    {/* Campo de pesquisa */}
                    <div className="flex-1 flex items-center gap-2 max-w-2xl mx-4">
                        <div className="flex-1 relative flex items-center border border-mono-300 dark:border-mono-700 rounded-lg bg-white dark:bg-mono-900 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
                            {/* Dropdown de filtro */}
                            <div className="relative" ref={filterDropdownRef}>
                                <button
                                    type="button"
                                    onClick={() => setIsFilterDropdownOpen(!isFilterDropdownOpen)}
                                    className="h-full px-3 py-2 flex items-center gap-2 border-r border-mono-300 dark:border-mono-700 bg-transparent hover:bg-mono-50 dark:hover:bg-mono-800 transition-colors"
                                >
                                    {searchFilter === 'all' ? (
                                        <MagnifyingGlass size={18} className="text-mono-600 dark:text-mono-400" />
                                    ) : (
                                        <span className="text-sm font-medium text-mono-700 dark:text-mono-300">
                                            {getFilterLabel(searchFilter)}
                                        </span>
                                    )}
                                    <CaretDown
                                        size={14}
                                        weight="bold"
                                        className={`text-mono-400 dark:text-mono-600 transition-transform ${isFilterDropdownOpen ? 'rotate-180' : ''}`}
                                    />
                                </button>

                                {/* Dropdown menu */}
                                {isFilterDropdownOpen && (
                                    <div className="absolute top-full left-0 mt-1 w-48 bg-white dark:bg-mono-950 border border-mono-200 dark:border-mono-800 rounded-lg shadow-lg z-50 overflow-hidden">
                                        {/* Opcao Todas */}
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setSearchFilter('all');
                                                setIsFilterDropdownOpen(false);
                                                setCurrentPage(1);
                                            }}
                                            className={`w-full flex items-center justify-between px-4 py-2 text-sm text-left hover:bg-mono-50 dark:hover:bg-mono-900 transition-colors ${
                                                searchFilter === 'all'
                                                    ? 'bg-blue-50 dark:bg-blue-950 text-blue-600 dark:text-blue-400'
                                                    : 'text-black dark:text-white'
                                            }`}
                                        >
                                            <span>Todas</span>
                                            {searchFilter === 'all' && (
                                                <span className="text-blue-600 dark:text-blue-400" />
                                            )}
                                        </button>

                                        {/* Opcao Tags */}
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setSearchFilter('tags');
                                                setIsFilterDropdownOpen(false);
                                                setCurrentPage(1);
                                            }}
                                            className={`w-full flex items-center justify-between px-4 py-2 text-sm text-left hover:bg-mono-50 dark:hover:bg-mono-900 transition-colors ${
                                                searchFilter === 'tags'
                                                    ? 'bg-blue-50 dark:bg-blue-950 text-blue-600 dark:text-blue-400'
                                                    : 'text-black dark:text-white'
                                            }`}
                                        >
                                            <span>Tags</span>
                                            {searchFilter === 'tags' && (
                                                <span className="text-blue-600 dark:text-blue-400" />
                                            )}
                                        </button>

                                        {/* Colunas de identificacao */}
                                        {identificationColumns.map(({ key, label }) => (
                                            <button
                                                key={key}
                                                type="button"
                                                onClick={() => {
                                                    setSearchFilter(key);
                                                    setIsFilterDropdownOpen(false);
                                                    setCurrentPage(1);
                                                }}
                                                className={`w-full flex items-center justify-between px-4 py-2 text-sm text-left hover:bg-mono-50 dark:hover:bg-mono-900 transition-colors ${
                                                    searchFilter === key
                                                        ? 'bg-blue-50 dark:bg-blue-950 text-blue-600 dark:text-blue-400'
                                                        : 'text-black dark:text-white'
                                                }`}
                                            >
                                                <span>{label}</span>
                                                {searchFilter === key && (
                                                    <span className="text-blue-600 dark:text-blue-400" />
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Input de pesquisa */}
                            <div className="flex-1 relative">
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        setCurrentPage(1); // Resetar para primeira pagina ao pesquisar
                                    }}
                                    placeholder={getSearchPlaceholder()}
                                    className={`w-full py-2 text-sm bg-transparent text-black dark:text-white placeholder-mono-400 dark:placeholder-mono-600 focus:outline-none pl-3 pr-10'
                  }`}
                                />
                                {searchTerm && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setSearchTerm('');
                                            setCurrentPage(1);
                                        }}
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center hover:bg-mono-50 dark:hover:bg-mono-800 transition-colors rounded-r-lg"
                                    >
                                        <XIcon
                                            size={16}
                                            className="text-mono-400 hover:text-mono-600 dark:hover:text-mono-300"
                                        />
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Botoes de acao */}
                    <div className="flex gap-2 flex-shrink-0">
                        {/* Botao Exportar */}
                        {(() => {
                            // Verificar se ha colunas de estoque no preset
                            const hasBlingStockColumn = visibleColumns.some(
                                (col) => col.key === 'blingStock' || col.key === 'brand',
                            );
                            const hasMlStockColumn = visibleColumns.some(
                                (col) =>
                                    col.key === 'mlStock' ||
                                    col.key === 'mlStockAvailable' ||
                                    col.key === 'mlStockNotAvailable',
                            );
                            const isStockLoading =
                                (hasBlingStockColumn || hasMlStockColumn) && (stockLoading || mlStockLoading);

                            return (
                                <button
                                    onClick={handleExport}
                                    disabled={isStockLoading}
                                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${
                                        isStockLoading
                                            ? 'bg-gray-400 dark:bg-gray-600 cursor-not-allowed'
                                            : 'bg-green-600 dark:bg-green-500 hover:bg-green-700 dark:hover:bg-green-600'
                                    }`}
                                    title={isStockLoading ? 'Aguarde o carregamento do estoque' : 'Exportar para Excel'}
                                >
                                    <DownloadSimple size={18} weight="bold" />
                                    {isStockLoading ? 'Carregando...' : 'Exportar Excel'}
                                </button>
                            );
                        })()}
                    </div>
                </div>

                {/* Tabela com scroll horizontal */}
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-mono-50 dark:bg-mono-950 border-b border-mono-200 dark:border-mono-800">
                            <tr>
                                {hasCartAction ? (
                                    <th className="w-12 px-2 py-3 text-center text-xs font-medium text-mono-600 dark:text-mono-400 uppercase tracking-wider sticky left-0 z-20 bg-mono-50 dark:bg-mono-950">
                                        OC
                                    </th>
                                ) : null}
                                {visibleColumns.map((column, index) => {
                                    const isFirstColumn = index === 0;
                                    const sortKey = column.type === 'predefined' ? column.key : `custom_${column.id}`;
                                    const label = column.type === 'predefined' ? column.label : column.name;
                                    const tooltip =
                                        column.type === 'predefined' ? column.description : column.expression;
                                    const firstColumnClass = hasCartAction ? 'sticky left-12 z-10' : 'sticky left-0 z-10';

                                    return (
                                        <React.Fragment key={column.id}>
                                            <SortableHeader
                                                column={sortKey}
                                                className={`${isFirstColumn ? `${firstColumnClass} bg-mono-50 dark:bg-mono-950 text-left` : 'text-center'}`}
                                                tooltip={tooltip}
                                            >
                                                {label}
                                            </SortableHeader>
                                            {/* Adicionar coluna Tags logo apos a primeira coluna */}
                                            {isFirstColumn && (
                                                <th className="px-3 py-3 text-left text-xs font-medium text-mono-600 dark:text-mono-400 uppercase tracking-wider bg-mono-50 dark:bg-mono-950 w-[200px]">
                                                    Tags
                                                </th>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tr>
                        </thead>

                        <tbody className="divide-y divide-mono-200 dark:divide-mono-800">
                            {currentPageProducts.map((product) => (
                                <tr
                                    key={product.itemId}
                                    className="hover:bg-mono-50 dark:hover:bg-mono-950 transition-colors"
                                >
                                    {hasCartAction ? (
                                        <td className="w-12 px-2 py-3 text-center sticky left-0 z-20 bg-white dark:bg-mono-950">
                                            <button
                                                type="button"
                                                onClick={() => openCartModal(product)}
                                                className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-mono-300 text-mono-700 transition-colors hover:bg-mono-100 dark:border-mono-700 dark:text-mono-300 dark:hover:bg-mono-900"
                                                title="Adicionar ao carrinho"
                                            >
                                                <Plus size={14} weight="bold" />
                                            </button>
                                        </td>
                                    ) : null}
                                    {visibleColumns.map((column, index) => {
                                        const isFirstColumn = index === 0;

                                        if (column.type === 'predefined') {
                                            // Coluna pre-definida
                                            if (column.key === 'title') {
                                                const itemId = product.itemId || product.item_id;
                                                return (
                                                    <React.Fragment key={column.id}>
                                                        <td
                                                            className={`px-3 py-3 text-sm text-black dark:text-white sticky ${hasCartAction ? 'left-12' : 'left-0'} z-10 bg-white dark:bg-mono-950 hover:bg-mono-50 dark:hover:bg-mono-950`}
                                                        >
                                                            <div className="flex items-start gap-2">
                                                                <a
                                                                    href={`https://www.mercadolivre.com.br/anuncios/lista?page=1&search=${itemId}&sort=DEFAULT`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="line-clamp-2 max-w-[250px] text-blue-600 dark:text-blue-400 hover:underline block overflow-hidden flex-1"
                                                                    style={{
                                                                        display: '-webkit-box',
                                                                        WebkitLineClamp: 2,
                                                                        WebkitBoxOrient: 'vertical',
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                    }}
                                                                    title={`Ver anuncio: ${product.title}`}
                                                                >
                                                                    {product.title}
                                                                </a>
                                                            </div>
                                                        </td>
                                                        {/* Coluna Tags apos a primeira coluna */}
                                                        {isFirstColumn && (
                                                            <td className="px-3 py-3 text-sm bg-white dark:bg-mono-950 hover:bg-mono-50 dark:hover:bg-mono-950">
                                                                <TagsColumn
                                                                    product={product}
                                                                    visibleColumns={visibleColumns}
                                                                    extractSellerSku={extractSellerSku}
                                                                />
                                                            </td>
                                                        )}
                                                    </React.Fragment>
                                                );
                                            }
                                            if (column.key === 'itemId') {
                                                const itemId = product.itemId || product.item_id;
                                                return (
                                                    <React.Fragment key={column.id}>
                                                        <td className="px-3 py-3 text-sm text-mono-700 dark:text-mono-300 text-left">
                                                            <span
                                                                className="font-mono text-xs"
                                                                title={itemId || 'MLB nao disponivel'}
                                                            >
                                                                {itemId || '-'}
                                                            </span>
                                                        </td>
                                                        {/* Coluna Tags apos a primeira coluna */}
                                                        {isFirstColumn && (
                                                            <td className="px-3 py-3 text-sm bg-white dark:bg-mono-950 hover:bg-mono-50 dark:hover:bg-mono-950">
                                                                <TagsColumn
                                                                    product={product}
                                                                    visibleColumns={visibleColumns}
                                                                    extractSellerSku={extractSellerSku}
                                                                />
                                                            </td>
                                                        )}
                                                    </React.Fragment>
                                                );
                                            }
                                            if (column.key === 'inventoryId') {
                                                return (
                                                    <React.Fragment key={column.id}>
                                                        <td className="px-3 py-3 text-sm text-mono-700 dark:text-mono-300 text-center">
                                                            {product.inventoryId || '-'}
                                                        </td>
                                                        {/* Coluna Tags apos a primeira coluna */}
                                                        {isFirstColumn && (
                                                            <td className="px-3 py-3 text-sm bg-white dark:bg-mono-950 hover:bg-mono-50 dark:hover:bg-mono-950">
                                                                <TagsColumn
                                                                    product={product}
                                                                    visibleColumns={visibleColumns}
                                                                    extractSellerSku={extractSellerSku}
                                                                />
                                                            </td>
                                                        )}
                                                    </React.Fragment>
                                                );
                                            }
                                            if (column.key === 'sku') {
                                                // Extrair seller_sku usando funcao helper
                                                const sellerSku = extractSellerSku(product);

                                                return (
                                                    <React.Fragment key={column.id}>
                                                        <td className="px-3 py-3 text-sm text-mono-700 dark:text-mono-300 text-left">
                                                            <span
                                                                className="font-mono text-xs"
                                                                title={sellerSku || 'SKU nao disponivel'}
                                                            >
                                                                {sellerSku || '-'}
                                                            </span>
                                                        </td>
                                                        {/* Coluna Tags apos a primeira coluna */}
                                                        {isFirstColumn && (
                                                            <td className="px-3 py-3 text-sm bg-white dark:bg-mono-950 hover:bg-mono-50 dark:hover:bg-mono-950">
                                                                <TagsColumn
                                                                    product={product}
                                                                    visibleColumns={visibleColumns}
                                                                    extractSellerSku={extractSellerSku}
                                                                />
                                                            </td>
                                                        )}
                                                    </React.Fragment>
                                                );
                                            }
                                            if (column.key === 'bestDay') {
                                                return (
                                                    <React.Fragment key={column.id}>
                                                        <td className="px-3 py-3 text-sm text-mono-700 dark:text-mono-300">
                                                            {product.bestDay?.date ? (
                                                                <div className="text-center">
                                                                    <div className="text-xs text-mono-500 dark:text-mono-500">
                                                                        {format(
                                                                            parseISO(product.bestDay.date),
                                                                            'dd/MM',
                                                                        )}
                                                                    </div>
                                                                    <div className="font-medium text-black dark:text-white">
                                                                        {product.bestDay.quantity}
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <div className="text-center">-</div>
                                                            )}
                                                        </td>
                                                        {/* Coluna Tags apos a primeira coluna */}
                                                        {isFirstColumn && (
                                                            <td className="px-3 py-3 text-sm bg-white dark:bg-mono-950 hover:bg-mono-50 dark:hover:bg-mono-950">
                                                                <TagsColumn
                                                                    product={product}
                                                                    visibleColumns={visibleColumns}
                                                                    extractSellerSku={extractSellerSku}
                                                                />
                                                            </td>
                                                        )}
                                                    </React.Fragment>
                                                );
                                            }
                                            if (column.key === 'blingStock') {
                                                const sellerSku = extractSellerSku(product);
                                                const stockInfo = getMapEntryBySku(stockData, sellerSku);

                                                return (
                                                    <td key={column.id} className="px-3 py-3 text-sm text-center">
                                                        {stockLoading ? (
                                                            <Skeleton className="h-4 w-1/2 mx-auto" />
                                                        ) : (
                                                            <span className="font-medium text-black dark:text-white">
                                                                {stockInfo?.quantity ?? 'N/A'}
                                                            </span>
                                                        )}
                                                    </td>
                                                );

                                                // <BlingStockColumn
                                                //   value={stockInfo.quantity}
                                                //   sellerSku={sellerSku}
                                                //   loading={stockLoading}
                                                //   error={stockInfo.error}
                                                //   onRefresh={handleRefreshStock}
                                                // />
                                            }
                                            if (column.key === 'brand') {
                                                const sellerSku = extractSellerSku(product);
                                                const stockInfo = getStockForSku(sellerSku);
                                                const brandLabel = stockInfo.brand || product.brand || '-';
                                                return (
                                                    <td key={column.id} className="px-3 py-3 text-sm text-center">
                                                        {stockLoading ? (
                                                            <Skeleton className="mx-auto h-4 w-2/3" />
                                                        ) : (
                                                            <span className="text-mono-700 dark:text-mono-300">
                                                                {brandLabel}
                                                            </span>
                                                        )}
                                                    </td>
                                                );
                                            }
                                            if (column.key === 'salesForecast') {
                                                return (
                                                    <td key={column.id} className="px-3 py-3 text-sm text-center">
                                                        <span className="font-medium text-black dark:text-white">
                                                            {Number(product.salesForecast || 0).toFixed(2)}
                                                        </span>
                                                    </td>
                                                );
                                            }
                                            if (column.key === 'mlStock') {
                                                const sellerSku = extractSellerSku(product);
                                                const stockInfo = getMapEntryBySku(mlStockMap, sellerSku);
                                                const total = stockInfo?.total ?? stockInfo?.quantity ?? 0;
                                                const available =
                                                    stockInfo?.available_quantity ?? stockInfo?.quantity ?? 0;
                                                const notAvailable = stockInfo?.not_available_quantity ?? 0;

                                                return (
                                                    <td key={column.id} className="px-3 py-3 text-sm text-center">
                                                        {mlStockLoading ? (
                                                            <Skeleton className="h-4 w-1/2 mx-auto" />
                                                        ) : (
                                                            <div className="group relative inline-block">
                                                                <span className="font-medium text-black dark:text-white cursor-help">
                                                                    {total}
                                                                </span>
                                                                {/* Tooltip - melhorado para permitir interacao */}
                                                                <div className="absolute bottom-full left-1/2 z-50 mb-2 hidden w-56 -translate-x-1/2 transform group-hover:block">
                                                                    <div className="bg-mono-900 dark:bg-mono-100 text-white dark:text-black text-xs rounded-lg py-2 px-3 shadow-xl">
                                                                        <div className="space-y-1">
                                                                            <div>
                                                                                Disponivel:{' '}
                                                                                <span className="font-semibold">
                                                                                    {available}
                                                                                </span>
                                                                            </div>
                                                                            <div>
                                                                                Nao Disponivel:{' '}
                                                                                <span className="font-semibold">
                                                                                    {notAvailable}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                        {/* <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedProductForModal({
                                            sku: sellerSku,
                                            name: product.title || product.name || sellerSku,
                                            mlStock: total,
                                            mlStockData: stockInfo,
                                            blingStock: getStockForSku(sellerSku).quantity || 0
                                          });
                                          setIsStockModalOpen(true);
                                          const newMap = new Map(visibleTooltips);
                                          newMap.set(tooltipId, false);
                                          setVisibleTooltips(newMap);
                                        }}
                                        className="mt-2 text-blue-300 dark:text-blue-600 hover:underline text-xs cursor-pointer"
                                      >
                                        Ver detalhes
                                      </button> */}
                                                                    </div>
                                                                    {/* Seta do tooltip */}
                                                                    <div className="absolute left-1/2 transform -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-mono-900 dark:border-t-mono-100" />
                                                                </div>
                                                            </div>
                                                        )}
                                                    </td>
                                                );
                                            }
                                            if (column.key === 'mlStockAvailable') {
                                                const sellerSku = extractSellerSku(product);
                                                const stockInfo = getMapEntryBySku(mlStockMap, sellerSku);
                                                const available =
                                                    stockInfo?.available_quantity ?? stockInfo?.quantity ?? 0;

                                                return (
                                                    <td key={column.id} className="px-3 py-3 text-sm text-center">
                                                        {mlStockLoading ? (
                                                            <Skeleton className="h-4 w-1/2 mx-auto" />
                                                        ) : (
                                                            <span className="font-medium text-green-600 dark:text-green-400">
                                                                {available || 0}
                                                            </span>
                                                        )}
                                                    </td>
                                                );
                                            }
                                            if (column.key === 'mlStockNotAvailable') {
                                                const sellerSku = extractSellerSku(product);
                                                const stockInfo = getMapEntryBySku(mlStockMap, sellerSku);
                                                const notAvailable = stockInfo?.not_available_quantity ?? 0;

                                                return (
                                                    <td key={column.id} className="px-3 py-3 text-sm text-center">
                                                        {mlStockLoading ? (
                                                            <Skeleton className="h-4 w-1/2 mx-auto" />
                                                        ) : (
                                                            <span className="font-medium text-red-600 dark:text-red-400">
                                                                {notAvailable || 0}
                                                            </span>
                                                        )}
                                                    </td>
                                                );
                                            }
                                            if (column.key === 'blingPrice') {
                                                // Extrair seller_sku usando funcao helper
                                                const sellerSku = extractSellerSku(product);
                                                const stockInfo = getStockForSku(sellerSku);
                                                const price = stockInfo.price || 0;

                                                return (
                                                    <td key={column.id} className="px-3 py-3 text-sm text-center">
                                                        <span className="text-mono-700 dark:text-mono-300">
                                                            {price > 0 ? `R$ ${price.toFixed(2)}` : '-'}
                                                        </span>
                                                    </td>
                                                );
                                            }
                                            if (column.key === 'blingCostPrice') {
                                                // Extrair seller_sku usando funcao helper
                                                const sellerSku = extractSellerSku(product);
                                                const stockInfo = getStockForSku(sellerSku);
                                                const costPrice = stockInfo.costPrice || 0;

                                                return (
                                                    <td key={column.id} className="px-3 py-3 text-sm text-center">
                                                        <span className="text-mono-700 dark:text-mono-300">
                                                            {costPrice > 0 ? `R$ ${costPrice.toFixed(2)}` : '-'}
                                                        </span>
                                                    </td>
                                                );
                                            }
                                            if (column.key === 'blingMargin') {
                                                // Extrair seller_sku usando funcao helper
                                                const sellerSku = extractSellerSku(product);
                                                const stockInfo = getStockForSku(sellerSku);
                                                const costPrice = stockInfo.costPrice || 0;

                                                // Preco ML
                                                const mlPrice = product.unit_price || product.price || 0;

                                                // Debug: Log para verificar valores
                                                if (process.env.NODE_ENV === 'development' && Math.random() < 0.05) {
                                                    console.log(' [Margem Debug]:', {
                                                        sku: sellerSku,
                                                        mlPrice,
                                                        costPrice,
                                                        stockInfo,
                                                        productKeys: Object.keys(product),
                                                    });
                                                }

                                                // Calcular margem: ((preco_ml - custo) / preco_ml) * 100
                                                let margin = null;
                                                if (mlPrice > 0 && costPrice > 0) {
                                                    margin = ((mlPrice - costPrice) / mlPrice) * 100;
                                                }

                                                return (
                                                    <td key={column.id} className="px-3 py-3 text-sm text-center">
                                                        {margin !== null ? (
                                                            <span
                                                                className={`font-medium ${
                                                                    margin >= 30
                                                                        ? 'text-green-600'
                                                                        : margin >= 20
                                                                          ? 'text-green-500'
                                                                          : margin >= 10
                                                                            ? 'text-yellow-600'
                                                                            : margin >= 0
                                                                              ? 'text-orange-600'
                                                                              : 'text-red-500'
                                                                }`}
                                                            >
                                                                {margin.toFixed(1)}%
                                                            </span>
                                                        ) : (
                                                            <span className="text-mono-400">-</span>
                                                        )}
                                                    </td>
                                                );
                                            }
                                            const value = product[column.key];
                                            return (
                                                <React.Fragment key={column.id}>
                                                    <td className="px-3 py-3 text-sm font-medium text-black dark:text-white text-center">
                                                        {value !== undefined && value !== null ? value : '-'}
                                                    </td>
                                                    {/* Coluna Tags apos a primeira coluna */}
                                                    {isFirstColumn && (
                                                        <td className="px-3 py-3 text-sm bg-white dark:bg-mono-950 hover:bg-mono-50 dark:hover:bg-mono-950">
                                                            <TagsColumn
                                                                product={product}
                                                                visibleColumns={visibleColumns}
                                                                extractSellerSku={extractSellerSku}
                                                            />
                                                        </td>
                                                    )}
                                                </React.Fragment>
                                            );
                                        }
                                        if (column.type === 'custom') {
                                            // Coluna customizada
                                            const value = product[`custom_${column.id}`];
                                            const error = product[`custom_${column.id}_error`];
                                            const displayValue = error
                                                ? 'Erro'
                                                : columnProcessor.getDisplayValue(value, column.format);

                                            return (
                                                <React.Fragment key={column.id}>
                                                    <td
                                                        className={`px-3 py-3 text-sm text-center ${error ? 'text-red-500 dark:text-red-400' : 'font-medium text-black dark:text-white'}`}
                                                        title={error || undefined}
                                                    >
                                                        {displayValue}
                                                    </td>
                                                    {/* Coluna Tags apos a primeira coluna */}
                                                    {isFirstColumn && (
                                                        <td className="px-3 py-3 text-sm bg-white dark:bg-mono-950 hover:bg-mono-50 dark:hover:bg-mono-950">
                                                            <TagsColumn
                                                                product={product}
                                                                visibleColumns={visibleColumns}
                                                                extractSellerSku={extractSellerSku}
                                                            />
                                                        </td>
                                                    )}
                                                </React.Fragment>
                                            );
                                        }

                                        return null;
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Paginacao */}
                <PaginationControls
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    itemsPerPage={itemsPerPage}
                    onItemsPerPageChange={(newValue) => {
                        setItemsPerPage(newValue);
                        setCurrentPage(1); // Resetar para primeira pagina
                    }}
                    totalItems={sortedProducts.length}
                />
            </TableShell>

            {/* Modals */}
            <Modal isOpen={cartModal.isOpen} onClose={closeCartModal} title="Adicionar ao carrinho">
                <div className="space-y-4">
                    <div className="space-y-1">
                        <p className="text-sm text-mono-600 dark:text-mono-400">Produto</p>
                        <p className="text-sm font-medium text-black dark:text-white">
                            {cartModal.product?.title || cartModal.product?.name || extractSellerSku(cartModal.product) || 'Produto'}
                        </p>
                        <p className="text-xs text-mono-500 dark:text-mono-500">
                            SKU: {extractSellerSku(cartModal.product) || 'Nao identificado'} | MLB:{' '}
                            {cartModal.product?.itemId || cartModal.product?.item_id || '-'}
                        </p>
                    </div>

                    <div className="space-y-2">
                        <label htmlFor="sellout-cart-quantity" className="block text-sm font-medium text-mono-700 dark:text-mono-300">
                            Quantidade
                        </label>
                        <input
                            id="sellout-cart-quantity"
                            type="number"
                            min={1}
                            value={cartModal.quantity}
                            onChange={(event) =>
                                setCartModal((current) => ({
                                    ...current,
                                    quantity: event.target.value,
                                }))
                            }
                            className="w-full rounded-lg border border-mono-300 bg-white px-3 py-2 text-sm text-black outline-none focus:border-black dark:border-mono-700 dark:bg-mono-900 dark:text-white dark:focus:border-white"
                        />
                    </div>

                    <div className="flex items-center justify-end gap-2">
                        <Button variant="secondary" onClick={closeCartModal}>
                            Cancelar
                        </Button>
                        <Button onClick={confirmAddToCart}>Adicionar</Button>
                    </div>
                </div>
            </Modal>

            <PresetModal
                isOpen={isPresetModalOpen}
                mode={editingPreset ? 'edit' : 'create'}
                preset={editingPreset}
                onClose={() => {
                    setIsPresetModalOpen(false);
                    setEditingPreset(null);
                }}
            />

            {/* <CustomColumnBuilder
        isOpen={isCustomColumnModalOpen}
        onClose={() => {
          setIsCustomColumnModalOpen(false);
        }}
        onSave={handleSaveCustomColumn}
      /> */}

            {/* Modal de Estoque */}
            {isStockModalOpen && selectedProductForModal && (
                <BlingStockModal
                    product={selectedProductForModal}
                    warehouses={[]}
                    onClose={() => {
                        setIsStockModalOpen(false);
                        setSelectedProductForModal(null);
                    }}
                />
            )}
        </>
    );
};

export default ProductAnalysisTable;
